import { AxiosError } from 'axios';

type JsonRecord = Record<string, unknown>;

const MESSAGE_KEYS = [
  'message',
  'Message',
  'error',
  'Error',
  'errors',
  'Errors',
  'title',
  'Title',
] as const;

const isRecord = (value: unknown): value is JsonRecord =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const collectMessages = (
  value: unknown,
  visited: Set<unknown>,
  depth = 0,
): string[] => {
  if (depth > 6 || value === null || value === undefined) return [];

  if (typeof value === 'string') {
    const message = value.trim();
    return message ? [message] : [];
  }

  if (Array.isArray(value)) {
    return value.flatMap((item) => collectMessages(item, visited, depth + 1));
  }

  if (!isRecord(value) || visited.has(value)) return [];
  visited.add(value);

  const prioritizedMessages = MESSAGE_KEYS.flatMap((key) =>
    key in value ? collectMessages(value[key], visited, depth + 1) : [],
  );

  if (prioritizedMessages.length > 0) return prioritizedMessages;

  return Object.values(value).flatMap((item) =>
    collectMessages(item, visited, depth + 1),
  );
};

export const extractApiErrorMessages = (payload: unknown): string[] =>
  Array.from(new Set(collectMessages(payload, new Set<unknown>())));

const getErrorCode = (payload: unknown): string | undefined => {
  if (!isRecord(payload)) return undefined;

  const code =
    payload.ErrorCode ?? payload.errorCode ?? payload.Code ?? payload.code;
  return typeof code === 'string' ? code : undefined;
};

const isAxiosErrorLike = (error: unknown): error is AxiosError<unknown> =>
  isRecord(error) && error.isAxiosError === true;

export class ApiError extends Error {
  readonly status?: number;
  readonly code?: string;
  readonly details?: unknown;
  readonly isNetworkError: boolean;
  readonly originalError: unknown;

  constructor(options: {
    message: string;
    status?: number;
    code?: string;
    details?: unknown;
    isNetworkError?: boolean;
    originalError: unknown;
  }) {
    super(options.message);
    this.name = 'ApiError';
    this.status = options.status;
    this.code = options.code;
    this.details = options.details;
    this.isNetworkError = options.isNetworkError ?? false;
    this.originalError = options.originalError;
    Object.setPrototypeOf(this, ApiError.prototype);
  }
}

export const normalizeApiError = (error: unknown): ApiError => {
  if (error instanceof ApiError) return error;

  if (isAxiosErrorLike(error)) {
    const payload = error.response?.data;
    const messages = extractApiErrorMessages(payload);
    const isNetworkError = !error.response;

    return new ApiError({
      message:
        messages.join('\n') ||
        (isNetworkError
          ? 'Unable to reach the server. Please check your connection and try again.'
          : error.message || 'The request could not be completed.'),
      status: error.response?.status,
      code: getErrorCode(payload),
      details: payload,
      isNetworkError,
      originalError: error,
    });
  }

  if (error instanceof Error) {
    return new ApiError({ message: error.message, originalError: error });
  }

  const messages = extractApiErrorMessages(error);
  return new ApiError({
    message: messages.join('\n') || 'The request could not be completed.',
    details: error,
    originalError: error,
  });
};
